import { Suspense, useEffect, useMemo } from 'react'
import { useParams, Navigate } from 'react-router'
import { usePageConfig } from '@hooks/usePageConfig'
import { useAppDispatch, useAppSelector } from 'universal-scripts'
import { clearToast, resetDashboardState } from 'src/store/slices/dashboard'
import { useDashboardHasChanges } from '@hooks/useDashboardChanges'
import { useUnsavedChangesWarning } from '@hooks/useUnsavedChangesWarning'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import Loader from '@components/Loader/Loader'
import Toast from '@components/Toast/Toast'
import BuilderHeader from './BuilderHeader/BuilderHeader'
import { BuilderAreaNew } from './BuilderArea/BuilderArea'
import WidgetCatalog from '@components/GridLayout/WidgetCatalog/WidgetCatalog'
import ItemEditor from '@components/GridLayout/ItemEditor/ItemEditor'
import DashboardGridSkeleton from '@components/skeletons/DashboardGridSkeleton/DashboardGridSkeleton'
import ResponsiveShield from '@components/ResponsiveShield/ResponsiveShield'
import ExistingDashboardGate from './ExistingDashboardGate'
import DashboardUnavailable from '@components/DashboardContent/DashboardUnavailable/DashboardUnavailable'

import './DashboardBuilder.css'

export default function DashboardBuilder() {
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const hasChanges = useDashboardHasChanges()
  const toastMessage = useAppSelector((state) => state.dashboard.toastMessage)

  const headerContent = useMemo(
    () => (
      <ErrorBoundary errorComponent={ErrorMessage}>
        <Suspense fallback={<Loader mini />}>
          <BuilderHeader />
        </Suspense>
      </ErrorBoundary>
    ),
    []
  )

  usePageConfig('dashboard-builder', headerContent)

  useEffect(() => {
    return () => {
      dispatch(resetDashboardState())
    }
  }, [dispatch])

  useUnsavedChangesWarning(hasChanges)

  if (!id) return <Navigate to="/dashboards" replace />

  const isNew = id === 'new'

  return (
    <>
      <WidgetCatalog />
      <section id="dashboard-builder-content" className="main-content">
        <ErrorBoundary errorComponent={DashboardUnavailable}>
          <Suspense fallback={<DashboardGridSkeleton />}>
            {isNew ? <BuilderAreaNew /> : <ExistingDashboardGate id={id} />}
          </Suspense>
        </ErrorBoundary>
      </section>
      <ItemEditor />
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => dispatch(clearToast())} />
      )}
      <ResponsiveShield />
    </>
  )
}
